import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, Lightbulb, Bug } from "lucide-react";

type FeedbackType = "suggestion" | "bug_report";

interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<"type" | "form" | "done">("type");
  const [type, setType] = useState<FeedbackType | null>(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/feedback", { type, subject, message });
    },
    onSuccess: () => {
      setStep("done");
    },
    onError: () => {
      toast({ title: "Failed to send. Please try again.", variant: "destructive" });
    },
  });

  function handleClose() {
    onClose();
    // Reset after animation
    setTimeout(() => {
      setStep("type");
      setType(null);
      setSubject("");
      setMessage("");
    }, 300);
  }

  function selectType(t: FeedbackType) {
    setType(t);
    setStep("form");
  }

  const canSubmit = subject.trim().length > 0 && message.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === "type" && "What's on your mind?"}
            {step === "form" && (type === "suggestion" ? "💡 Suggest a Feature" : "🐛 Report a Problem")}
            {step === "done" && "Thanks!"}
          </DialogTitle>
        </DialogHeader>

        {step === "type" && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              onClick={() => selectType("suggestion")}
              className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-colors text-center"
            >
              <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-full">
                <Lightbulb className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="font-semibold text-sm">Suggest a Feature</p>
                <p className="text-xs text-muted-foreground mt-0.5">Got an idea?</p>
              </div>
            </button>
            <button
              onClick={() => selectType("bug_report")}
              className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-colors text-center"
            >
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <Bug className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="font-semibold text-sm">Report a Problem</p>
                <p className="text-xs text-muted-foreground mt-0.5">Something broken?</p>
              </div>
            </button>
          </div>
        )}

        {step === "form" && (
          <div className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label htmlFor="fb-subject">Subject</Label>
              <Input
                id="fb-subject"
                placeholder={type === "suggestion" ? "e.g. Dark mode for quiz" : "e.g. Quiz won't load"}
                value={subject}
                onChange={(e) => setSubject(e.target.value.slice(0, 100))}
                maxLength={100}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fb-message">
                {type === "suggestion" ? "Describe your idea" : "What happened?"}
              </Label>
              <Textarea
                id="fb-message"
                placeholder={
                  type === "suggestion"
                    ? "Tell us what you'd like and why it would be helpful..."
                    : "Tell us what went wrong and what you were doing..."
                }
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
                className="resize-none h-28"
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground text-right">{message.length}/1000</p>
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setStep("type")} className="flex-1">
                Back
              </Button>
              <Button
                onClick={() => mutation.mutate()}
                disabled={!canSubmit || mutation.isPending}
                className="flex-1"
              >
                {mutation.isPending ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="font-semibold">We got your message!</p>
              <p className="text-sm text-muted-foreground mt-1">
                {type === "suggestion"
                  ? "Thanks for the idea, the team will check it out."
                  : "Thanks for letting us know, we'll look into it."}
              </p>
            </div>
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
